/**
 * The admin's light and dark themes. Colors are CSS variables (--a-*), set on
 * the root element for the chosen theme, so every screen switches at once and
 * styles written once (react-jss) don't need to know the theme.
 *
 * The page being edited, previews and anything else that shows the live site
 * sit inside `data-gx-theme="light"`: the site has one look, whatever the admin's.
 */

export type ThemePref = 'light' | 'dark' | 'system';
export type ThemeName = 'light' | 'dark';

/** Where the choice is kept in the browser. */
export const THEME_STORAGE_KEY = 'gx-admin-theme';

type Palette = Record<string, string>;

export const LIGHT: Palette = {
  bg: '#EDEFF1',
  chrome: '#F8F9FA',
  chromeHover: '#E6E9EC',
  line: '#DADDE2',
  field: '#FFFFFF',
  fieldLine: '#CBD0D6',
  fg: '#171A20',
  fgHover: '#2E323A',
  fgActive: '#000000',
  onFg: '#FFFFFF',
  muted: '#6B6D72',
  wall: '#C3C7CD',
  wallGrid: 'rgba(23, 26, 32, 0.08)',
  clay: '#9A6633',
  clayInk: '#6B4520',
  claySoft: 'rgba(154, 102, 51, 0.08)',
  green: '#1B8F4C',
  amber: '#B0720A',
  red: '#C83737',
  redHover: '#AE2D2D',
  blue: '#2A62A6',
  surface: '#FFFFFF',
  rowLine: '#ECEEF1',
  rowHover: '#F6F7F9',
  rowSelected: '#EEF2F8',
  scrim: 'rgba(23, 26, 32, 0.40)',
  pop: '0 14px 36px rgba(23, 26, 32, 0.16), 0 2px 6px rgba(23, 26, 32, 0.08)',
  toneGreenFg: '#146B3A',
  toneGreenBg: 'rgba(27, 143, 76, 0.10)',
  toneAmberFg: '#7F5208',
  toneAmberBg: 'rgba(176, 114, 10, 0.12)',
  toneRedFg: '#A12B2B',
  toneRedBg: 'rgba(200, 55, 55, 0.10)',
  toneBlueFg: '#21508A',
  toneBlueBg: 'rgba(42, 98, 166, 0.10)',
  toneGrayFg: '#54575D',
  toneGrayBg: 'rgba(23, 26, 32, 0.06)',
  toneGrayDot: '#9A9DA3'
};

/**
 * Same roles in the dark: near-black graphite chrome, slightly lighter work
 * surfaces, light type; lamp colors brightened to keep their contrast.
 */
export const DARK: Palette = {
  bg: '#0D0F12',
  chrome: '#121418',
  chromeHover: '#23262C',
  line: '#2C3038',
  field: '#1D2025',
  fieldLine: '#3B414A',
  fg: '#E7E9EC',
  fgHover: '#FFFFFF',
  fgActive: '#C9CDD3',
  onFg: '#121418',
  muted: '#9BA1AA',
  wall: '#0A0B0D',
  wallGrid: 'rgba(255, 255, 255, 0.05)',
  clay: '#D19A63',
  clayInk: '#E9BE8E',
  claySoft: 'rgba(209, 154, 99, 0.12)',
  green: '#3CC27A',
  amber: '#E3A43A',
  red: '#F0716F',
  redHover: '#F48C8A',
  blue: '#6EA2E6',
  surface: '#191C21',
  rowLine: '#24282E',
  rowHover: '#1F2329',
  rowSelected: '#1E2938',
  scrim: 'rgba(0, 0, 0, 0.60)',
  pop: '0 14px 36px rgba(0, 0, 0, 0.55), 0 2px 6px rgba(0, 0, 0, 0.40)',
  toneGreenFg: '#5ED494',
  toneGreenBg: 'rgba(60, 194, 122, 0.16)',
  toneAmberFg: '#F0BE62',
  toneAmberBg: 'rgba(227, 164, 58, 0.16)',
  toneRedFg: '#F59B99',
  toneRedBg: 'rgba(240, 113, 111, 0.16)',
  toneBlueFg: '#9CC1F0',
  toneBlueBg: 'rgba(110, 162, 230, 0.16)',
  toneGrayFg: '#B5BAC1',
  toneGrayBg: 'rgba(255, 255, 255, 0.08)',
  toneGrayDot: '#80868F'
};

/** `var(--a-name)`: use these in styles; the value follows the theme. */
export const themeVar = (name: string): string => `var(--a-${name})`;

const declarations = (p: Palette) => Object.keys(p).map(k => `--a-${k}:${p[k]}`).join(';');

/**
 * The variables for both themes. Light is the default and also applies inside
 * any `data-gx-theme="light"` element, which keeps live-site content light in a
 * dark admin. `color-scheme` makes native controls and scrollbars follow.
 */
export const THEME_CSS = [
  `:root{${declarations(LIGHT)};color-scheme:light}`,
  `[data-gx-theme="dark"]{${declarations(DARK)};color-scheme:dark}`,
  `[data-gx-theme="light"]{${declarations(LIGHT)};color-scheme:light}`,
  // Site content inside a dark admin: the text color the site expects, not the admin's light text.
  '[data-gx-theme="dark"] [data-gx-theme="light"]{color:var(--a-fg)}',
  // Behind everything (overscroll, gaps): dark in the dark theme; light keeps the browser default as before.
  'html[data-gx-theme="dark"] body{background:var(--a-chrome)}'
].join('\n');

/**
 * Runs in the page head before anything draws, so a dark choice doesn't flash
 * light first. Light unless the browser has a saved choice.
 */
export const THEME_INIT_SCRIPT = `(function(){try{var p=localStorage.getItem(${JSON.stringify(THEME_STORAGE_KEY)});`
  + `var d=p==='dark'||(p==='system'&&window.matchMedia&&matchMedia('(prefers-color-scheme: dark)').matches);`
  + `document.documentElement.setAttribute('data-gx-theme',d?'dark':'light');}catch(e){document.documentElement.setAttribute('data-gx-theme','light');}})();`;
